// src/pages/admin/AdminDashboard.jsx
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import API from '../../api';
import { getIntegrationStatus } from '../../api/integrations';
import { useCurrentUser } from '../../context/UserContext';
import {
  ChartPanel,
  CompareBar,
  CompositionDonut,
  ScoreGauge,
  CHART_COLORS,
} from '../../components/charts';
import { localizeStudentsByBranch, localizeHealthMix } from '../../utils/chartSeries';

const DISMISS_EMAIL_ALERT_KEY = 'admin_dismiss_email_setup_alert';
const DISMISS_WHATSAPP_ALERT_KEY = 'admin_dismiss_whatsapp_setup_alert';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useCurrentUser();
  const [totalStudentCount, setTotalStudentCount] = useState(null);
  const [activeBranchCount, setActiveBranchCount] = useState(null);
  const [activeEnrollmentCount, setActiveEnrollmentCount] = useState(null);
  const [activeCoachCount, setActiveCoachCount] = useState(null);
  const [atRiskStudentCount, setAtRiskStudentCount] = useState(null);
  const [healthMix, setHealthMix] = useState([]);
  const [studentsByBranch, setStudentsByBranch] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoadError, setStatsLoadError] = useState(false);
  const [dismissedEmailAlert, setDismissedEmailAlert] = useState(
    () => sessionStorage.getItem(DISMISS_EMAIL_ALERT_KEY) === '1'
  );
  const [dismissedWhatsAppAlert, setDismissedWhatsAppAlert] = useState(
    () => sessionStorage.getItem(DISMISS_WHATSAPP_ALERT_KEY) === '1'
  );
  const [emailOperational, setEmailOperational] = useState(true);
  const [whatsappOperational, setWhatsappOperational] = useState(true);

  const showEmailAlert = !dismissedEmailAlert && !emailOperational;
  const showWhatsAppAlert = !dismissedWhatsAppAlert && !whatsappOperational;

  useEffect(() => {
    (async () => {
      try {
        const status = await getIntegrationStatus();
        setEmailOperational(!!status?.email?.operational);
        setWhatsappOperational(!!status?.whatsapp?.operational);
      } catch {
        // Don't block dashboard if integrations API is unavailable
      }
    })();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setStatsLoadError(false);
      try {
        const { data } = await API.get('/admin/dashboard/stats');
        setTotalStudentCount(data.studentCount ?? 0);
        setActiveBranchCount(data.branchCount ?? 0);
        setActiveEnrollmentCount(data.enrollmentCount ?? 0);
        setActiveCoachCount(data.coachCount ?? 0);
        setAtRiskStudentCount(data.atRiskStudentCount ?? 0);
        const mix = Array.isArray(data.healthMix) ? data.healthMix : [];
        setHealthMix(localizeHealthMix(mix, t).map((row) => ({
          ...row,
          color: row.atRisk ? CHART_COLORS.danger : CHART_COLORS.success,
        })));
        setStudentsByBranch(localizeStudentsByBranch(
          Array.isArray(data.studentsByBranch) ? data.studentsByBranch : [],
          t,
        ));
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setTotalStudentCount('—');
        setActiveBranchCount('—');
        setActiveEnrollmentCount('—');
        setActiveCoachCount('—');
        setAtRiskStudentCount('—');
        setHealthMix([]);
        setStudentsByBranch([]);
        setStatsLoadError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [t]);

  const MetricCard = ({ title, value, icon, gradient, onClick }) => (
    <div
      className={`bg-gradient-to-br ${gradient} rounded-2xl shadow-lg p-6 text-white cursor-pointer hover:shadow-xl transition-all transform hover:scale-105`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm opacity-90 mb-1">{title}</div>
          <div className="text-4xl font-bold">{loading ? '...' : value}</div>
        </div>
        <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
          {icon}
        </div>
      </div>
    </div>
  );

  const QuickActionCard = ({ title, description, icon, onClick, color }) => (
    <button
      onClick={onClick}
      className="bg-white rounded-xl shadow-md p-5 hover:shadow-lg transition-all text-left border border-gray-100 hover:border-gray-200 w-full"
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 bg-gradient-to-br ${color} rounded-lg flex items-center justify-center flex-shrink-0`}>
          {icon}
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </button>
  );

  return (
    <div className="w-full space-y-6">

        {statsLoadError && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="status">
            {t('pages.adminDashboard.statsLoadFailed', {
              defaultValue: 'Dashboard stats could not be loaded. Figures below may be incomplete — refresh to try again.',
            })}
          </div>
        )}

        {showEmailAlert && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-amber-900">
              <strong>{t('pages.adminDashboard.emailAlertTitle')}</strong> {t('pages.adminDashboard.emailAlertBody')}
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <Link to="/admin/email-settings" className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700">
                {t('pages.adminDashboard.configureEmail')}
              </Link>
              <button
                type="button"
                onClick={() => {
                  sessionStorage.setItem(DISMISS_EMAIL_ALERT_KEY, '1');
                  setDismissedEmailAlert(true);
                }}
                className="text-xs font-medium text-amber-800 hover:underline"
              >
                {t('common.dismiss')}
              </button>
            </div>
          </div>
        )}

        {showWhatsAppAlert && (
          <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-sky-900">
              <strong>{t('pages.adminDashboard.whatsappAlertTitle')}</strong> {t('pages.adminDashboard.whatsappAlertBody')}
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <Link to="/admin/whatsapp" className="rounded-lg bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-sky-700">
                {t('pages.adminDashboard.configureWhatsApp')}
              </Link>
              <button
                type="button"
                onClick={() => {
                  sessionStorage.setItem(DISMISS_WHATSAPP_ALERT_KEY, '1');
                  setDismissedWhatsAppAlert(true);
                }}
                className="text-xs font-medium text-sky-800 hover:underline"
              >
                {t('common.dismiss')}
              </button>
            </div>
          </div>
        )}

        {/* Welcome Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {t('dashboard.welcome', { name: user?.firstName || t('dashboard.defaultAdmin') })}
          </h1>
          <p className="text-gray-600 mt-2">
            {t('pages.adminDashboard.subtitle')}
          </p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-6">
          <MetricCard
            title={t('pages.adminDashboard.totalStudents')}
            value={totalStudentCount !== null ? totalStudentCount : '0'}
            gradient="from-blue-500 to-blue-600"
            onClick={() => navigate('/admin/students')}
            icon={
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
              </svg>
            }
          />

          <MetricCard
            title={t('pages.adminDashboard.activeBranches')}
            value={activeBranchCount !== null ? activeBranchCount : '0'}
            gradient="from-green-500 to-emerald-600"
            onClick={() => navigate('/admin/branches')}
            icon={
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            }
          />

          <MetricCard
            title={t('pages.adminDashboard.activeCoaches')}
            value={activeCoachCount !== null ? activeCoachCount : '0'}
            gradient="from-amber-500 to-orange-600"
            onClick={() => navigate('/admin/coaches')}
            icon={
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            }
          />

          <MetricCard
            title={t('pages.adminDashboard.atRiskStudents')}
            value={atRiskStudentCount !== null ? atRiskStudentCount : '0'}
            gradient="from-red-500 to-rose-600"
            onClick={() => navigate('/admin/at-risk')}
            icon={
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            }
          />

          <MetricCard
            title={t('pages.adminDashboard.activeEnrollments')}
            value={activeEnrollmentCount !== null ? activeEnrollmentCount : '0'}
            gradient="from-purple-500 to-purple-600"
            onClick={() => navigate('/admin/enrollments')}
            icon={
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
          />
        </div>

        {/* Insights */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          <ChartPanel
            title={t('charts.studentHealth', { defaultValue: 'Student health' })}
            subtitle={t('charts.studentHealthHint', { defaultValue: 'At-risk vs healthy roster' })}
            actionTo="/admin/at-risk"
            actionLabel={t('charts.viewAtRisk', { defaultValue: 'View at-risk' })}
            loading={loading}
            empty={!loading && healthMix.every((r) => !Number(r.value))}
            emptyMessage={t('charts.noHealthData', { defaultValue: 'No student health data yet' })}
            error={statsLoadError ? t('pages.adminDashboard.statsLoadFailed', { defaultValue: 'Stats unavailable' }) : null}
            height={240}
          >
            <CompositionDonut
              data={healthMix}
              chartSize={180}
              centerValue={totalStudentCount !== null && totalStudentCount !== '—' ? totalStudentCount : '—'}
              centerLabel={t('pages.adminDashboard.totalStudents')}
            />
          </ChartPanel>

          <ChartPanel
            title={t('charts.studentsByBranch', { defaultValue: 'Students by branch' })}
            subtitle={t('charts.studentsByBranchHint', {
              defaultValue: 'Enrolled students per branch (includes not enrolled)',
            })}
            actionTo="/admin/branches"
            actionLabel={t('charts.manageBranches', { defaultValue: 'Branches' })}
            loading={loading}
            empty={!loading && studentsByBranch.length === 0}
            emptyMessage={t('charts.noBranchData', { defaultValue: 'No branch enrollment data yet' })}
            error={statsLoadError ? t('pages.adminDashboard.statsLoadFailed', { defaultValue: 'Stats unavailable' }) : null}
            height={240}
          >
            <CompareBar
              data={studentsByBranch.slice(0, 8)}
              height={Math.max(180, studentsByBranch.length * 36)}
              layout={studentsByBranch.length > 1 ? 'horizontal' : 'vertical'}
              labelMaxLen={22}
            />
          </ChartPanel>

          <ChartPanel
            title={t('charts.atRiskShare', { defaultValue: 'At-risk share' })}
            subtitle={t('charts.atRiskShareHint', { defaultValue: 'Percent of roster needing attention' })}
            actionTo="/admin/at-risk"
            actionLabel={t('charts.open', { defaultValue: 'Open' })}
            loading={loading}
            empty={!loading && (totalStudentCount == null || totalStudentCount === '—' || Number(totalStudentCount) === 0)}
            emptyMessage={t('charts.noAtRiskData', { defaultValue: 'No at-risk data to chart yet' })}
            height={240}
          >
            <ScoreGauge
              value={
                totalStudentCount && totalStudentCount !== '—' && Number(totalStudentCount) > 0
                  ? (Number(atRiskStudentCount || 0) / Number(totalStudentCount)) * 100
                  : 0
              }
              label={t('pages.adminDashboard.atRiskStudents')}
              sublabel={
                atRiskStudentCount != null && atRiskStudentCount !== '—'
                  ? t('charts.atRiskCount', {
                    count: atRiskStudentCount,
                    defaultValue: '{{count}} students',
                  })
                  : undefined
              }
              color={Number(atRiskStudentCount || 0) > 0 ? CHART_COLORS.danger : undefined}
            />
          </ChartPanel>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h2 className="text-xl font-bold text-gray-900 mb-4">{t('dashboard.quickActions')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <QuickActionCard
              title={t('pages.adminDashboard.addStudent')}
              description={t('pages.adminDashboard.addStudentDesc')}
              color="from-blue-500 to-blue-600"
              onClick={() => navigate('/admin/students/new')}
              icon={
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              }
            />

            <QuickActionCard
              title={t('pages.adminDashboard.createBatch')}
              description={t('pages.adminDashboard.createBatchDesc')}
              color="from-green-500 to-green-600"
              onClick={() => navigate('/admin/batches/new')}
              icon={
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              }
            />

            <QuickActionCard
              title={t('pages.adminDashboard.walletTopup')}
              description={t('pages.adminDashboard.walletTopupDesc')}
              color="from-purple-500 to-purple-600"
              onClick={() => navigate('/admin/advance-payment')}
              icon={
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              }
            />

            <QuickActionCard
              title={t('pages.adminDashboard.manageCoaches')}
              description={t('pages.adminDashboard.manageCoachesDesc')}
              color="from-orange-500 to-orange-600"
              onClick={() => navigate('/admin/coaches')}
              icon={
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              }
            />

            <QuickActionCard
              title={t('pages.adminDashboard.viewSessions')}
              description={t('pages.adminDashboard.viewSessionsDesc')}
              color="from-pink-500 to-pink-600"
              onClick={() => navigate('/admin/sessions')}
              icon={
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              }
            />

            <QuickActionCard
              title={t('pages.adminDashboard.paymentReports')}
              description={t('pages.adminDashboard.paymentReportsDesc')}
              color="from-cyan-500 to-cyan-600"
              onClick={() => navigate('/admin/reports')}
              icon={
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
          </div>
        </div>
    </div>
  );
};

export default AdminDashboard;
